import React, { useEffect, useState, useCallback } from 'react';
import { reduxForm, InjectedFormProps } from 'redux-form';
import { Modal, Spinner, Alert } from 'react-bootstrap';
import { withRouter, RouteComponentProps } from 'react-router';
import { PartnersAPI, SurveysAPI } from "../../API";

// Enum for page status
export enum PageStatus {
    None = 'NONE',
    Loading = 'LOADING',
    Loaded = 'LOADED',
    Error = 'ERROR',
    Submitting = 'SUBMITTING',
    Submitted = 'SUBMITTED'
}


export type FormValue = {
    name?: string;
    description?: string;
    partnerId?: string;
    surveyId?: string;
    successUrl: string;
    overQuotaUrl: string;
    disqualifiedUrl: string;
    badTerminatedUrl: string;
    quota: string;
    surveyType?: string;
    surveyStatus?: string;
};

interface FormProps extends InjectedFormProps<FormValue>, RouteComponentProps {
    onClose: () => void;
    onSubmit: () => void;
    show: boolean;
    partnerId: string;
    partnerName: string;
    surveyId: string;
    surveyStatus: string;
    modalType: string;
}

const Form: React.FC<FormProps> = ({
    handleSubmit,
    onSubmit,
    onClose,
    show,
    partnerId,
    surveyId,
    initialize,
    partnerName,
    surveyStatus,
    modalType,
    reset
}) => {
    const [status, setStatus] = useState < PageStatus > (PageStatus.None);
    const [error, setError] = useState < string | null > (null);
    const [formValues, setFormValues] = useState < FormValue > ({
        partnerId,
        surveyId,
        successUrl: '',
        overQuotaUrl: '',
        disqualifiedUrl: '',
        badTerminatedUrl: '',
        quota: '',
        surveyType: surveyStatus,
    });
    const [quotaLimit, setQuotaLimit] = useState < number > (0);

    useEffect(() => {
        if (partnerId) {
            fetchDetails();
        }
    }, [partnerId]);

    const fetchDetails = useCallback(async () => {
        setStatus(PageStatus.Loading);
        try {
            if (!partnerId) throw new Error('Invalid ID');
            const data = await PartnersAPI.getUrlOnTheSurveyBasis(partnerId,surveyId);
            const leftQuotaPoints = await SurveysAPI.getLeftQuotaPoints({id: surveyId});
            if(!leftQuotaPoints.country){ alert("You haven't provide any IPolls quota as the selected country is India."); }
            setQuotaLimit(data.quota ? parseInt(data.quota) + leftQuotaPoints.countLeft : leftQuotaPoints.countLeft);            
            const initialValues: FormValue = {
                name: data.name || '',
                description: data.description || '',
                partnerId,
                surveyId,
                successUrl: data.successUrl || '',
                overQuotaUrl: data.overQuotaUrl || '',
                disqualifiedUrl: data.disqualifiedUrl || '',
                badTerminatedUrl: data.badTerminatedUrl || '',
                quota: data.quota || '',
                surveyType: data.surveyType || surveyStatus,
            };
            setFormValues(initialValues);
            initialize(initialValues);
            setStatus(PageStatus.Loaded);
        } catch (error) {
            setError(error.message || 'An error occurred');
            setStatus(PageStatus.Error);
        }
    }, [partnerId, initialize]);

    const onSubmitForm = async (event: React.FormEvent) => {
        event.preventDefault();
        setStatus(PageStatus.Submitting);
        try {
            const response = await modalType == "create" ? PartnersAPI.createOnthebasisofSurvey(formValues) : PartnersAPI.updateOnthebasisofSurvey(formValues);
            onSubmit(response.id);
            setStatus(PageStatus.Submitted);
        } catch (error) {
            setError(error.message || 'An error occurred');
            setStatus(PageStatus.Error);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        if(name == "quota"){
            if(quotaLimit < parseInt(value)){
                alert(`Quota value can't be greater than ${quotaLimit}`);
                return;
            }
        }
        setFormValues(prevValues => ({ ...prevValues, [name]: value }));
    };

    if (!show) return null;

    return (
        <Modal
            centered
            size="lg"
            backdrop="static"
            onHide={onClose}
            show={show}
            style={{ zIndex: 1201 }}
        >
            <Modal.Header closeButton>
                <Modal.Title>{modalType == "create" ? "Add" : "Update"} Partner Name: <span className='text-info'>{partnerName}</span></Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '78vh', overflow: 'auto' }}>
                {status === PageStatus.Loading && (
                    <div className="d-flex justify-content-center w-100 p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                )}
                {status === PageStatus.Error && (
                    <Alert variant="danger">{error}</Alert>
                )}
                <form onSubmit={onSubmitForm}>
                    <div className="form-group">
                        <label htmlFor="successUrl">Completed URL*</label>
                        <input
                            name="successUrl"
                            type="text"
                            className="form-control"
                            placeholder="Enter..."
                            value={formValues.successUrl}
                            onChange={handleInputChange}
                            required
                        />
                        <label htmlFor="overQuotaUrl">Quota Full URL*</label>
                        <input
                            name="overQuotaUrl"
                            type="text"
                            className="form-control"
                            placeholder="Enter..."
                            value={formValues.overQuotaUrl}
                            onChange={handleInputChange}
                            required
                        />
                        <label htmlFor="disqualifiedUrl">Terminate URL*</label>
                        <input
                            name="disqualifiedUrl"
                            type="text"
                            className="form-control"
                            placeholder="Enter..."
                            value={formValues.disqualifiedUrl}
                            onChange={handleInputChange}
                            required
                        />
                        <label htmlFor="badTerminatedUrl">Bad Terminate URL*</label>
                        <input
                            name="badTerminatedUrl"
                            type="text"
                            className="form-control"
                            placeholder="Enter..."
                            value={formValues.badTerminatedUrl}
                            onChange={handleInputChange}
                            required
                        />
                        <label htmlFor="quota">Quota*</label>
                        <input
                            name="quota"
                            type="number"
                            className="form-control"
                            placeholder="Enter..."
                            value={formValues.quota}
                            onChange={handleInputChange}
                            required
                            min={0}
                        />
                        <label htmlFor="description">Survey Status*</label><br />                  
                            <div className="custom-control custom-radio custom-control-inline">
                            <input
                                type="radio"
                                id="surveyType5"
                                name="surveyType5"
                                checked={formValues.surveyType === 'Test'}
                                className="custom-control-input"
                                onChange={() => setFormValues(prevValues => ({ ...prevValues, surveyType: 'Test' }))}
                            />
                            <label className="custom-control-label" htmlFor="surveyType5">Test</label>
                            </div>

                            <div className="custom-control custom-radio custom-control-inline">
                            <input
                                type="radio"
                                id="surveyType1"
                                name="surveyType1"
                                className="custom-control-input"
                                checked={formValues.surveyType === 'Open'}
                                onChange={() => setFormValues(prevValues => ({ ...prevValues, surveyType: 'Open' })) }
                                disabled={surveyStatus == "Test"}
                            />
                            <label className="custom-control-label" htmlFor="surveyType1">Live</label>
                            </div>

                            <div className="custom-control custom-radio custom-control-inline">
                            <input
                                type="radio"
                                id="surveyType3"
                                name="surveyType3"
                                checked={formValues.surveyType === 'Hold'}
                                className="custom-control-input"
                                onChange={() => setFormValues(prevValues => ({ ...prevValues, surveyType: 'Hold' })) }
                                disabled={surveyStatus == "Test"}
                            />
                            <label className="custom-control-label" htmlFor="surveyType3">On Hold</label>
                            </div>
                    </div>
                    <hr />
                    {status === PageStatus.Error && (
                        <Alert variant="danger" className="mt-2">
                            {error}
                        </Alert>
                    )}
                    <div className="d-flex align-items-center mt-2">
                        <button
                            type="submit"
                            disabled={status === PageStatus.Submitting}
                            className="btn btn-primary mr-3"
                        >
                            Submit
                        </button>
                        <button
                            type="button"
                            className="btn btn-light mr-3"
                            onClick={() => {
                                setFormValues({
                                    successUrl: '',
                                    overQuotaUrl: '',
                                    disqualifiedUrl: '',
                                    badTerminatedUrl: '',
                                    quota: '',
                                });
                                reset(); // Reset form using redux-form's reset action
                            }}
                        >
                            Reset
                        </button>
                        {status === PageStatus.Submitting && (
                            <Spinner animation="border" variant="primary" />
                        )}
                    </div>
                </form>
            </Modal.Body>
        </Modal>
    );
};

const FormRedux = reduxForm < FormValue > ({
    form: 'partnerForm', // Ensure this is unique for each form
    enableReinitialize: true,
})(Form);

const serveFormWithRouter = withRouter(FormRedux);

export { serveFormWithRouter as Form };
